import { useState, type FormEvent, type SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { DialogLayer } from '../ui/Dialog';
import { trapTab } from '../ui/focus';
import { daysText } from './conflicts';

/**
 * „Dni rezerwacji…” of a packlist or quote line (owner 2026-10-24 15:29 / 15:35: „muszę móc dodać rezerwację dzień przed
 * i dzień po oraz ewentualnie daty wybrane”; „Info o zajętości dłuższej na czerwono wyraźnie”). Shared by the packlist
 * and the quote: the dialog and the red note under the line's name. They never change a price.
 */
export type ExtraDays = { before: boolean; after: boolean; dates: string[] };

const short = (day: string) => `${Number(day.slice(8, 10))}.${day.slice(5, 7)}`;

/** „zajęte także 6.11, 8.11” — the days held beyond the line's own (empty: none). */
export function extraDaysText(held: readonly string[]): string {
  return held.length === 0 ? '' : `zajęte także ${held.map(short).join(', ')}`;
}

/** The red note under the line's name; nothing when no extra day is held. Seen by everyone who sees the line. */
export function ExtraDaysNote({ held }: { held: readonly string[] | undefined }) {
  if (!held || held.length === 0) return null;
  return (
    <span className="extra-days" data-feature-id="packlists.extra_days" title="Sprzęt jest zarezerwowany także w te dni („Dni rezerwacji…”); cena się nie zmienia">
      {extraDaysText(held)}
    </span>
  );
}

/**
 * The dialog: „dzień przed”, „dzień po” and „Wybrane dni…” (any days, one by one). `onSave` gets null when nothing is
 * ticked or chosen; it answers false when nothing was saved (e.g. „Anuluj” in the conflict question) — the dialog stays.
 */
export function ExtraDaysDialog({ name, value, onSave, onClose }: { name: string; value: ExtraDays | null | undefined; onSave: (value: ExtraDays | null) => Promise<boolean>; onClose: () => void }) {
  const [before, setBefore] = useState(value?.before ?? false);
  const [after, setAfter] = useState(value?.after ?? false);
  const [dates, setDates] = useState<string[]>(value?.dates ?? []);
  const [day, setDay] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function addDay() {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return;
    setDates((d) => [...new Set([...d, day])].sort());
    setDay('');
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const empty = !before && !after && dates.length === 0;
      if (await onSave(empty ? null : { before, after, dates })) onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nie udało się zapisać');
    } finally {
      setBusy(false);
    }
  }

  // over everything (a portal); the row it was opened from must not take its clicks or its Enter (React bubbles through
  // portals): Escape still reaches the dialog layers on the document
  const keep = (e: SyntheticEvent) => e.stopPropagation();
  return createPortal(
    <div className="dialog-backdrop" onClick={keep} onMouseDown={keep} onContextMenu={keep} onKeyDown={(e) => e.key !== 'Escape' && e.stopPropagation()}>
      <form
        className="dialog panel form extra-days-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="extra-days-title"
        data-feature-id="packlists.extra_days"
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={busy} />
        <h2 id="extra-days-title">Dni rezerwacji: {name}</h2>
        <p className="muted">Sprzęt będzie zajęty także w te dni (np. wysyłka dzień wcześniej, odbiór dzień później). Cena się nie zmienia.</p>
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <label className="check">
          <input type="checkbox" checked={before} onChange={(e) => setBefore(e.target.checked)} />
          dzień przed
        </label>
        <label className="check">
          <input type="checkbox" checked={after} onChange={(e) => setAfter(e.target.checked)} />
          dzień po
        </label>
        <fieldset className="extra-days-dates">
          <legend>Wybrane dni…</legend>
          <span className="extra-days-add">
            <input
              type="date"
              aria-label="Wybrany dzień"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addDay();
                }
              }}
            />
            <button className="btn small" type="button" disabled={day === ''} onClick={addDay}>
              Dodaj dzień
            </button>
          </span>
          {dates.length > 0 && (
            <ul className="extra-days-list">
              {dates.map((d) => (
                <li key={d} className="chip">
                  {daysText(d, d)}
                  <button className="btn icon ghost" type="button" aria-label={`Usuń dzień ${daysText(d, d)}`} onClick={() => setDates((all) => all.filter((x) => x !== d))}>
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
        <div className="actions">
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
